/**
 * One inbound photo inside a message bubble.
 *
 * Before this existed the inbox could only print «مرفق — افتح المحادثة في تطبيق Airbnb لعرضه» and
 * send staff to the Airbnb app. On prod EVERY photo message carries zero text, so those bubbles read
 * as blank — which is what makes this a credibility problem rather than a convenience one.
 *
 * ⚠️ THE IMAGE IS FETCHED, NOT `src`-ed. Auth rides in a header that an `<img>` tag cannot send, so
 * the bytes come through the authenticated API client and are wrapped in an object URL here. The
 * provider's own URL never exists in this browser — see conversationApi.attachment().
 *
 * ⚠️ THIS COMPONENT IS BUILT NOT TO THROW, rather than built to be caught. The error boundary that
 * protects this pane sits around the whole thread display, and it is a last resort — it also cannot
 * help with the failure that actually matters here: a truncated or corrupt image does NOT raise a
 * React error, it simply fails to decode. That is what `onError` below is for, and it is the reason
 * every step in here degrades to the honest label instead of relying on containment.
 *
 * Visual language is deliberately IDENTICAL to `.inbox-auto-photo`, the photo our check-in
 * automation sends. A guest's photo and ours are the same kind of object in a thread; giving them
 * different treatments would imply a distinction that does not exist for the person reading it.
 */
import React, { useState, useEffect, useRef } from 'react';
import { conversationApi } from '../../services/conversationApi';

/**
 * Hard ceiling on how many images one message may draw.
 *
 * The browser is told a COUNT by the server and never the locations, so this is the only place that
 * number can be sanity-checked. Every attachment message on prod carries exactly one; five is
 * already generous. The point is not to predict the real maximum — it is that a provider bug, a bad
 * migration or a corrupted row must never turn one message into an unbounded render loop.
 */
export const MAX_ATTACHMENTS = 5;

/**
 * The honest fallback — the same label the bubble showed before images rendered.
 * Exported because InboxPage renders it too: with the feature switched off the fetching component is
 * never mounted at all, so the label needs one definition both sites can share.
 */
export function AttachmentLabel({ text }) {
  return (
    <span className="inbox-attachment">
      <i className="bi bi-paperclip" aria-hidden="true"></i>
      {text}
    </span>
  );
}

function OneAttachment({ conversationId, messageId, index, fallbackLabel }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Held in a ref as well as state so cleanup can revoke the CURRENT url without listing it as an
  // effect dependency — which would re-run the fetch every time the url changed and loop.
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    // Drop whatever is on screen the moment the TARGET changes. The cleanup below revokes the old
    // object URL, so leaving it in state would leave the bubble pointing at a released blob — a
    // broken image — until the replacement lands.
    setUrl(null);
    setFailed(false);

    conversationApi
      .attachment(conversationId, messageId, index)
      .then((blob) => {
        // A thread switched mid-flight must not paint into the new thread, and must not leak the
        // blob it just created either.
        if (cancelled) return;
        // Guarded: createObjectURL throws on anything that is not a Blob, and this value came from
        // the network. A throw here would escape into render, which is exactly what this component
        // is written to avoid.
        try {
          const objectUrl = URL.createObjectURL(blob);
          urlRef.current = objectUrl;
          setUrl(objectUrl);
        } catch {
          setFailed(true);
        }
      })
      .catch(() => {
        // Backend said no — flag off (403), gone (404), or upstream failure (502). All three mean
        // the same thing to the operator: no picture, keep the honest label.
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      // ⚠️ NOT optional. An inbox tab stays open all shift; an un-revoked object URL is a leak that
      // grows with every photo scrolled past.
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [conversationId, messageId, index]);

  if (failed) return <AttachmentLabel text={fallbackLabel} />;

  // Reserve the space rather than collapsing it, so the thread does not jump when the photo lands.
  if (!url) {
    return (
      <div className="inbox-attachment-loading" role="status" aria-label="جارٍ تحميل الصورة">
        <i className="bi bi-image" aria-hidden="true"></i>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`inbox-attachment-photo${expanded ? ' is-expanded' : ''}`}
      onClick={() => setExpanded((v) => !v)}
      aria-label={expanded ? 'تصغير الصورة' : 'تكبير الصورة'}
    >
      <img
        src={url}
        alt="صورة مرسلة في المحادثة"
        loading="lazy"
        // ⚠️ THE FAILURE THAT ACTUALLY HAPPENS. Bytes arrived, but they do not decode — truncated
        // JPEG, corrupt payload, an encoding this browser will not take. None of that raises a React
        // error, so no boundary above would ever see it; without this handler the operator gets a
        // broken-image icon, which reads as "the PMS is broken" rather than "open it in Airbnb".
        // Logged, because the whole point of the rollout is telling "nothing failed" apart from
        // "every third image failed", and a silent fallback makes those identical.
        onError={() => {
          console.warn(`[INBOX-ATTACHMENT] image failed to decode — message ${messageId} index ${index}`);
          setFailed(true);
        }}
      />
    </button>
  );
}

/**
 * @param {string} conversationId
 * @param {string} messageId
 * @param {number} count          how many images this message has (never their locations)
 * @param {string} fallbackLabel  the platform-aware «مرفق …» line
 *
 * Mounted ONLY when the feature is on — see InboxPage. A flag that gated behaviour while still
 * running the component would protect the network call and nothing else: a render or lifecycle bug
 * would execute either way, which is false reassurance rather than a kill switch.
 */
export default function MessageAttachment({ conversationId, messageId, count, fallbackLabel }) {
  // Normalise before rendering anything. `count` is a number the SERVER believes; a non-integer, a
  // negative or an implausible value must degrade to the label, never drive a loop.
  const n = Number.isInteger(count) && count > 0 ? Math.min(count, MAX_ATTACHMENTS) : 0;
  if (n === 0) return <AttachmentLabel text={fallbackLabel} />;

  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        <OneAttachment
          key={i}
          conversationId={conversationId}
          messageId={messageId}
          index={i}
          fallbackLabel={fallbackLabel}
        />
      ))}
    </>
  );
}
